import React, {useState} from 'react';
import ReactMarkdown from 'react-markdown';
import {FaBookmark, FaRegBookmark, FaEyeSlash, FaEye} from 'react-icons/fa';
import './Flashcard.css';
import {HOST} from "../config";
import {markdownComponents} from "../markdownImage";

const progressLabels = {
    0: 'Not learned',
    1: 'Incorrect',
    2: 'Unsure',
    3: '1x Correct',
    4: '2x Correct',
    5: '3x Correct',
};

const progressMapping = {
    'untouched': 0,
    'incorrect': 1,
    'unsure': 2,
    'correct1': 3,
    'correct2': 4,
    'correct3': 5,
};

const objectFlip = (obj) => {
    const ret = {};
    Object.keys(obj).forEach(key => {
        ret[obj[key]] = key;
    });
    return ret;
};

const cardContextLabel = (card) => {
    const parts = [];
    if (card.theme) parts.push(card.theme.toUpperCase());
    if (card.bookTitle) parts.push(card.bookTitle.replace(/–\s+Flashcards$/, ''));
    else if (card.topic) parts.push(card.topic);
    if (card.subTopic) parts.push(card.subTopic);
    return parts.join(' · ');
};

const Flashcard = ({card, interactive = true, onHide, flipped: controlledFlipped, onFlip}) => {
    // In overview mode, allow local control of bookmark and progress
    const [bookmarked, setBookmarked] = useState(card.bookmarked || false);
    const [hidden, setHidden] = useState(card.hidden || false);
    const [progress, setProgress] = useState(
        // Expect progress to be a numeric value between 0 and 3
        card.progress !== undefined ? progressMapping[card.progress] : 0
    );

    // Reveal state is controlled by the caller (e.g. Listen Mode) when
    // flipped/onFlip are passed in; otherwise it's managed internally as before.
    const isControlled = controlledFlipped !== undefined;
    const [internalFlipped, setInternalFlipped] = useState(false);
    const flipped = isControlled ? controlledFlipped : internalFlipped;

    React.useEffect(() => {
        if (!isControlled) setInternalFlipped(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [card.front])

    if (interactive) {

        const handleFlip = () => {
            if (isControlled) {
                onFlip(!flipped);
            } else {
                setInternalFlipped(!flipped);
            }
        };

        const handleHide = (e) => {
            e.stopPropagation();
            if (onHide) onHide();
        };

        return (
            <div className="flashcard-container" onClick={handleFlip}>
                <button className="hide-button" onClick={handleHide} title="Hide this card from now on">
                    <FaEyeSlash/>
                </button>
                <div className={`flashcard ${flipped ? 'flipped' : ''}`}>
                    <div className="card-face front">
                        {cardContextLabel(card) && <div className="card-context">{cardContextLabel(card)}</div>}
                        <ReactMarkdown components={markdownComponents}>{card.front}</ReactMarkdown>
                        {!flipped && <div className="flip-hint">Tap to reveal answer</div>}
                    </div>
                    <div className="card-face back">
                        <ReactMarkdown components={markdownComponents}>{card.back}</ReactMarkdown>
                    </div>
                </div>
            </div>
        );
    } else {
        // Overview mode: show both sides along with interactive controls.
        const toggleBookmark = () => {
            // Here you can also perform an API call to update bookmark state.
            fetch(HOST + `/api/cards/${card._id}/bookmark`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({bookmarked: !bookmarked}),
            })
                .then(response => {
                    if (!response.ok) {
                        alert("Error");
                        throw new Error('Error updating bookmark status.');
                    }
                    return response.json();
                })
                .then(data => {
                    setBookmarked(!bookmarked);
                })
                .catch(err => {
                    console.error(err);
                });
        };

        const toggleHidden = () => {
            fetch(HOST + `/api/cards/${card._id}/hidden`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({hidden: !hidden}),
            })
                .then(response => {
                    if (!response.ok) {
                        alert("Error");
                        throw new Error('Error updating hidden status.');
                    }
                    return response.json();
                })
                .then(() => {
                    setHidden(!hidden);
                })
                .catch(err => {
                    console.error(err);
                });
        };

        const handleProgressChange = (e) => {
            // Here you can also send the new progress value to your server.
            const value = e.target.value;
            fetch(HOST + `/api/cards/${card._id}/progress`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({progress: objectFlip(progressMapping)[parseInt(value, 10)]}),
            })
                .then(response => {
                    if (!response.ok) {
                        alert("Error");
                        throw new Error('Error updating progress.');
                    }
                    return response.json();
                })
                .then(data => {
                    setProgress(parseInt(value, 10));
                    console.log('Progress updated:', data);
                })
                .catch(err => {
                    console.error(err);
                });
        };

        return (
            <div className="flashcard-overview">
                <div className="card-meta">
                    <button className="bookmark-button" onClick={toggleBookmark}>
                        {bookmarked ? (
                            <FaBookmark title="Remove Bookmark"/>
                        ) : (
                            <FaRegBookmark title="Add Bookmark"/>
                        )}
                    </button>
                    <button className="hide-toggle-button" onClick={toggleHidden}>
                        {hidden ? (
                            <FaEyeSlash title="Unhide this card"/>
                        ) : (
                            <FaEye title="Hide this card"/>
                        )}
                    </button>
                    <div className="progress-slider">
                        <select
                            id={`progress-${card.id}`}
                            value={progress}
                            onChange={handleProgressChange}
                        >
                            {Object.entries(progressLabels).map(([value, label]) => (
                                <option key={value} value={value}>{label}</option>
                            ))}
                        </select>
                    </div>
                </div>
                <div className="overview-content">
                    <div className="flashcard-side front">
                        <ReactMarkdown components={markdownComponents}>{card.front}</ReactMarkdown>
                    </div>
                    <div className="flashcard-side back" style={{transform: 'none'}}>
                        <ReactMarkdown components={markdownComponents}>{card.back}</ReactMarkdown>
                    </div>
                </div>
            </div>
        );
    }
};

export default Flashcard;
